import type { AppItemBreakdown } from "@/features/app-studio/api/appBreakdownsApi";
import type { AppQueryBuilderCompileRequest } from "@/features/app-studio/api/appQueryBuilderApi";
import type { AppS3SourceConfig } from "@/features/app-studio/api/appS3SourcesApi";
import type {
  AppDrilldownItemType,
  AppDrilldownPresentation,
} from "@/features/app-studio/api/appKpisApi";
import type { AppWidgetInputMode } from "@/features/app-studio/lib/appChartLayout";
import type { AppComparisonPeriods } from "@/features/app-studio/lib/comparisonDisplay";
import type {
  APP_TABLE_AGGREGATE,
  APP_TABLE_PINNED_CELL_TYPE,
  APP_TABLE_PINNED_ROW_POSITION,
} from "@/constants";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";
import type { ValueFormat } from "@/shared/lib/valueFormat";

/**
 * One configured column: the source key plus an optional display-name override.
 *
 * Configured, not displayed — `visible` is what decides whether it renders. An approval table can
 * carry a column purely to write it out to storage.
 */
export type AppTableColumnConfig = {
  key: string;
  label?: string | null;
  /**
   * Whether this column renders. Absent means visible, so every config stored before
   * approval-only columns existed still reads as "this list is the displayed columns".
   */
  visible?: boolean;
  /**
   * Approval tables only: whether this column is written to the S3 object when a viewer
   * approves the row. Absent means every displayed column, which is what approving wrote
   * before the setting existed. Independent of `visible` — a column can be written out
   * without ever being on screen.
   */
  include_in_approval?: boolean;
  /**
   * Set when this column's values are URLs: every cell shows this text as a link that opens its
   * own row's URL in a new tab. Absent renders the raw value, as every column did before.
   */
  link_text?: string | null;
  /**
   * How this column's values are displayed: a symbol, thousands separators and decimals for
   * numbers, or a date pattern. Display only -- sorting, filters and drill-downs use the raw
   * value. A download writes the cell in it too, as the table shows it. Absent shows the raw value,
   * as every column did before. Read it through `parseValueFormat`.
   */
  format?: ValueFormat | null;
};

export type AppTableSortConfig = {
  key: string;
  direction: "asc" | "desc";
};

/**
 * Everything an approval table configures beyond the switch itself. Two independent settings share
 * one stored blob, and **either may stand alone**.
 *
 * The *classification taxonomy* is `source` plus the two ordered level lists, where a level-2
 * value is only valid under a particular level-1 value and a level-3 value only under a particular
 * pair. Both arrays are ordered and **the index is the level** — entry 0 is level 1.
 * `level_properties` names properties on the taxonomy objects; `level_columns` names the widget
 * columns they drive, index-aligned. `source` is a file selection under the domain's `inputs/`
 * prefix, the same shape a file-backed table widget stores, so the existing picker works on it
 * unchanged.
 *
 * `identity_columns` is the **row key**, named outright instead of inferred — see the field.
 *
 * `hide_rows_approved_on_ids` is the other setting: **other tables in this app whose approvals also
 * hide rows here**. Approvals are per widget, so a row approved on another table would otherwise
 * stay on screen in this one for ever.
 */
export type AppTableApprovalConfig = {
  /** Absent on a config that carries only `hide_rows_approved_on_ids`. */
  source?: AppS3SourceConfig | null;
  level_properties?: string[];
  level_columns?: string[];
  /** Whether each level constrains the next, or the levels are independent of one another. */
  hierarchical?: boolean;
  /**
   * The row key, named outright. Empty or absent means identity is *inferred* — every approval
   * column, minus the level columns and minus the ones the table does not display — which is what
   * every config stored before this field existed says. Named, those columns and nothing else
   * decide whether two rows are the same row.
   */
  identity_columns?: string[];
  /** Ids of other approval tables in this app. Absent or empty when none are configured. */
  hide_rows_approved_on_ids?: number[];
};

/**
 * A *complete* taxonomy, or no taxonomy and at least one followed table — the same all-or-nothing
 * rule the API enforces. Any number of levels, but the two lists must line up: everything reads
 * them by index.
 *
 * This guard is load-bearing beyond type safety. It runs inside `isAppTableSummary`, so rejecting
 * a config rejects the whole summary, and `fetchAppTables` then throws for the entire list: the
 * builder's item panel fails to load with "Unexpected response shape", and the surfaces that
 * swallow that error render as though the app had no widgets at all. A guard stricter than what
 * the API can store therefore takes out the builder, not just one field — which is precisely why
 * the levels-less shape has to be accepted here the moment the API can store one.
 *
 * `hierarchical` is deliberately not required: a config stored before that field existed does not
 * carry it, and demanding it would reject exactly those.
 */
export function isAppTableApprovalConfig(
  value: unknown,
): value is AppTableApprovalConfig {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  const isLevelList = (entry: unknown): entry is string[] =>
    Array.isArray(entry) &&
    entry.length > 0 &&
    entry.every((item) => typeof item === "string");
  const followsTables =
    row.hide_rows_approved_on_ids === undefined ||
    (Array.isArray(row.hide_rows_approved_on_ids) &&
      row.hide_rows_approved_on_ids.every((item) => typeof item === "number"));
  if (!followsTables) {
    return false;
  }
  const keysRows =
    row.identity_columns === undefined ||
    (Array.isArray(row.identity_columns) &&
      row.identity_columns.every((item) => typeof item === "string"));
  if (!keysRows) {
    return false;
  }
  // Truthiness, not presence — the exact test the API's own all-or-nothing rule applies. A stored
  // config is dumped with every field, so one carrying no taxonomy still arrives as
  // `source: null` with two empty level lists. Reading those as a *signal* sent every such config
  // down the branch below, where `source !== null` then rejected it — and rejecting a config
  // rejects the whole summary, which fails the builder's item panel with "Unexpected response
  // shape" rather than losing one field.
  const hasTaxonomySignal =
    Boolean(row.source) ||
    (Array.isArray(row.level_properties) && row.level_properties.length > 0) ||
    (Array.isArray(row.level_columns) && row.level_columns.length > 0);
  if (!hasTaxonomySignal) {
    // No taxonomy at all. Valid only as the carrier for one of the other settings — an entirely
    // empty config is stored as null, so one arriving here says nothing.
    return (
      (Array.isArray(row.hide_rows_approved_on_ids) &&
        row.hide_rows_approved_on_ids.length > 0) ||
      (Array.isArray(row.identity_columns) && row.identity_columns.length > 0)
    );
  }
  return (
    typeof row.source === "object" &&
    row.source !== null &&
    isLevelList(row.level_properties) &&
    isLevelList(row.level_columns) &&
    row.level_properties.length === row.level_columns.length
  );
}

/**
 * Descriptor for one *block* of a table widget's rows, mirroring the backend's
 * `AppTablePaginationResponse`. A block is up to a thousand rows, not one page: the browser slices
 * it into 10-row pages itself and only comes back when a page turn runs off the end of it.
 *
 * `enabled=false` means no rows were fetched — an errored widget, or SQL that failed to prepare.
 * `supported=false` marks a widget pagination doesn't apply to at all (an S3-file-backed table),
 * which keeps the classic unbounded grid.
 */
/**
 * One line of a table export's NDJSON response.
 *
 * Exactly one `columns` line arrives first -- even for an empty result, so a zero-row export still
 * produces a valid header-only file -- then zero or more `rows` lines, then exactly one terminal
 * line. An `error` may arrive *after* some `rows` lines when the warehouse fails part-way through,
 * and a client that has already buffered rows at that point must download nothing rather than a
 * silently truncated file.
 */
export type AppTableExportLine =
  | { type: "columns"; columns: string[] }
  | { type: "rows"; rows: Record<string, unknown>[] }
  | { type: "done"; row_count: number }
  | { type: "error"; message: string };

export type AppTablePagination = {
  enabled: boolean;
  supported?: boolean;
  /** Whether another block follows this one. Derived server-side from one extra fetched row, so
   * it is exact and can never disagree with the rows on screen. */
  has_more?: boolean;
  /** Total across the whole result. Present on the first block only — later blocks omit it, and
   * the client keeps the one it captured for the session. */
  total_rows?: number | null;
  /** Opaque bookmark for the next block; store and replay it verbatim. Null on the last block. */
  next_cursor?: string | null;
  /**
   * The column sort the server ordered this block by, first entry first. Absent from a server that
   * predates server-side sorting, which only ever applied the widget's saved sort in the browser.
   */
  sort?: AppTableSortConfig[] | null;
  /**
   * The aggregates the widget's pinned rows show, over every row of the result. On the first block
   * only, and only when the result runs past it: a result that fits in one block is all here, and
   * the browser aggregates it itself. Absent or null otherwise, and when the server could not
   * compute them -- those cells are then left blank rather than total one block.
   */
  column_aggregates?: AppTableColumnAggregate[] | null;
};

/**
 * Draws a table on its side: each source row becomes a column, each shown column becomes a row,
 * and the first (id) column holds those columns' header text.
 *
 * The rotated columns are headed by `headings` where the author typed one, else by
 * `header_column`'s values, else by their row number.
 */
export type AppTableRotateConfig = {
  /**
   * The source column whose values head the rotated columns, consumed rather than shown as a row.
   * Absent for a result with no column fit to head them -- one row of totals, say.
   */
  header_column?: string | null;
  /** Header of the id column. Absent shows the header column's own label. */
  label?: string | null;
  /**
   * The author's own heading for each rotated column, by position: the first heads the first
   * column after the id column. A blank entry, or a column past the end, keeps its default.
   */
  headings?: string[];
};

/** What a pinned row can calculate over a column; see `APP_TABLE_AGGREGATE`. */
export type AppTableAggregate = (typeof APP_TABLE_AGGREGATE)[keyof typeof APP_TABLE_AGGREGATE];

export type AppTablePinnedRowPosition =
  (typeof APP_TABLE_PINNED_ROW_POSITION)[keyof typeof APP_TABLE_PINNED_ROW_POSITION];

export type AppTablePinnedCellType =
  (typeof APP_TABLE_PINNED_CELL_TYPE)[keyof typeof APP_TABLE_PINNED_CELL_TYPE];

/**
 * One cell of a pinned row that shows something other than the row's own `aggregate`.
 *
 * `type` decides what: `aggregate` calculates `aggregate` over `source_column` -- the cell's own
 * `column` when absent, otherwise any column of the result, a hidden one too -- and `text` shows
 * `text` as written. An empty `text` leaves the cell blank, which is how a column is kept out of
 * the row's own calculation.
 */
export type AppTablePinnedCellConfig = {
  /** The shown column this cell sits under. */
  column: string;
  type: AppTablePinnedCellType;
  aggregate?: AppTableAggregate | null;
  source_column?: string | null;
  text?: string | null;
};

/**
 * A row kept in view above or below the table's rows: a totals row, say. Never one of the page's
 * rows and never sorted with them, it stays on screen on every page.
 *
 * `aggregate` is calculated under every shown column with no cell of its own, over the whole
 * result; `label` goes in the first shown column unless that column has a cell of its own. A
 * rotated table calculates each cell over the rotated column above it -- one source row -- so only
 * `label` and `aggregate` apply to it, unless `column` is set. Read it through `tablePinnedRowsOf`,
 * never directly.
 */
export type AppTablePinnedRowConfig = {
  label?: string | null;
  position: AppTablePinnedRowPosition;
  aggregate?: AppTableAggregate | null;
  cells?: AppTablePinnedCellConfig[];
  /**
   * Rotated tables only: the column whose row this is -- its own values, one per rotated column,
   * kept in view like a second header and taken out of the rows that scroll. Any column of the
   * result, a hidden one too. `aggregate` and `cells` do not apply while it is set, and `label`
   * defaults to the column's own header. A table drawn the usual way round has no such row, so
   * there it falls back to `label`, `aggregate` and `cells`.
   */
  column?: string | null;
};

/** One column aggregate the server computed over a table's whole result, for its pinned rows. */
export type AppTableColumnAggregate = {
  aggregate: AppTableAggregate;
  column: string;
  /** A number, or the column's own value for `min`/`max` of text or dates; null when empty. */
  value: unknown;
};

/**
 * Presentation config layered on the query result at render time (never changes the
 * query): which columns show (ordered), header-name overrides, an optional sort, an optional
 * rotation, how many leading columns stay in view, and rows such as totals pinned above or below.
 *
 * `sort` is a list, first entry sorting first. A config saved before multi-column sort holds a
 * single object instead and is never migrated, so read it through `tableSortOf`, never directly.
 */
export type AppTableDisplayConfig = {
  columns: AppTableColumnConfig[];
  sort?: AppTableSortConfig[] | AppTableSortConfig | null;
  /** Absent or null draws the table the usual way round. Read it through `tableRotateOf`. */
  rotate?: AppTableRotateConfig | null;
  /**
   * How many leading columns stay in view while the grid scrolls sideways. Absent means 0, none;
   * read it through `tableFrozenColumnCount`.
   */
  frozen_columns?: number | null;
  /** Absent or empty pins no rows. Read it through `tablePinnedRowsOf`. */
  pinned_rows?: AppTablePinnedRowConfig[] | null;
};

/**
 * One clickable column of a table: clicking a cell in `trigger_column` opens widget
 * `(item_type, item_id)` filtered by that row's values in every `value_columns` entry, ANDed.
 */
export type AppTableDrilldown = {
  id: number;
  trigger_column: string;
  value_columns: string[];
  /**
   * The semantic dimension each `value_columns` entry resolved to on save, positionally aligned
   * with it — how the server filters the target. Read-only and server-derived. A `null` entry means
   * that column matched no dimension, so its filter falls back to matching the target query's own
   * output columns — narrower, since it can then only reach a column the target actually selects.
   */
  dimension_names?: (string | null)[];
  item_type: AppDrilldownItemType;
  item_id: number;
  /**
   * How the target opens: a full-screen dialog, or a panel down the right-hand edge that leaves
   * the widget that was clicked visible beside it. Absent on a row stored before the setting
   * existed, which means the dialog — the only thing a drill-down did then.
   */
  presentation?: AppDrilldownPresentation;
};

export type AppTableSummary = {
  id: number;
  app_id: number;
  /** The table's SQL lives on its referenced AppQuery; not returned on the summary. */
  sql?: string | null;
  /** Reference to the reusable AppQuery that supplies this table's SQL. */
  query_id?: number | null;
  description: string | null;
  display_config?: AppTableDisplayConfig | null;
  column_span: number;
  sort_order: number;
  tab_id?: number | null;
  input_mode: AppWidgetInputMode;
  input_config?: AppQueryBuilderCompileRequest | null;
  /** False when the author hid this table's AI summary; absent on rows from older servers. */
  show_summary?: boolean;
  /** True when published-app viewers can approve this table's rows out to S3. */
  approval_enabled?: boolean;
  /** The stored approval config, so the builder repopulates its form without a second request. */
  approval_config?: AppTableApprovalConfig | null;
  /** Configured clickable columns, so the config UI needs no second round trip. */
  drilldowns?: AppTableDrilldown[];
  created_at: string;
  updated_at: string;
};

export type AppTableDataItem = {
  id: number;
  /**
   * The saved query this widget renders, when it has one. Present on the data item so a runtime
   * view can tell that two widgets are backed by the same query — and therefore hold the same
   * rows, which is what lets a drill-down between them be served locally. Absent for a widget
   * with inline SQL, which is shared with nothing by construction.
   */
  query_id?: number | null;
  description?: string | null;
  display_config?: AppTableDisplayConfig | null;
  column_span: number;
  sort_order: number;
  tab_id?: number | null;
  columns: string[];
  rows: Record<string, unknown>[];
  error?: string | null;
  /** Display names of active tab filters that did not apply to this widget (drives the badge). */
  ignored_filters?: string[] | null;
  /** Reason code for `ignored_filters`; "file_source" for a table that reads JSON files. */
  ignored_filters_reason?: string | null;
  /**
   * True when this widget is some KPI card's drill-down widget: it is excluded from the tab
   * grid and only rendered inside the drill-down dialog.
   */
  is_drilldown_target?: boolean;
  /**
   * Clickable columns of this table. Present on the data item because the runtime views render
   * cells straight from this payload and never fetch the summary.
   */
  drilldowns?: AppTableDrilldown[];
  /**
   * Whether the published view offers the approve action for this table. On the data item because
   * the runtime views render from this payload alone and never fetch the summary.
   */
  approval_enabled?: boolean;
  /**
   * The table columns the taxonomy dropdowns edit, in level order; empty or absent when the table
   * has no taxonomy. Only the column keys travel — the runtime needs to know which cells open a
   * dropdown and nothing else, and this payload is served to every viewer.
   */
  approval_taxonomy_columns?: string[];
  /**
   * The columns an author named as the table's row key, or empty/absent when identity is inferred
   * from `display_config` the way it always was. Sent rather than inferred because the viewer never
   * sees `approval_config` — and a viewer keying rows differently from the server would hide the
   * wrong ones on approve.
   */
  approval_identity_columns?: string[];
  /**
   * Ids of the widgets whose approvals also hide rows here. The server applies this on every read,
   * so the viewer needs it for one case only: an approval made on a sibling *during this page's
   * life* has not been through a read yet, and without knowing who this widget follows there is no
   * way to tell whether that approval was one of its own rows.
   */
  approval_follows_ids?: number[];
  /** False when the author hid this table's AI summary; see `AppChartDataItem.show_summary`. */
  show_summary?: boolean;
  /**
   * The stored AI summary. `undefined` on the published stream's `item` frame, where it arrives in a
   * later `item_summary` frame; `null` means either not generated yet, or generated and found
   * unsummarisable (an errored widget or one with no rows).
   */
  summary?: string | null;
  /** Block descriptor for these rows. Defaults to `{ enabled: false }` when absent. */
  pagination?: AppTablePagination;
  /** See `AppChartDataItem.breakdown` — the breakdown and the option these rows were run for. */
  breakdown?: AppItemBreakdown | null;
  /** See `AppChartDataItem.comparison_periods`. */
  comparison_periods?: AppComparisonPeriods | null;
};

export type AppTablesDataResponse = {
  tables: AppTableDataItem[];
};

function isAppTableSummary(value: unknown): value is AppTableSummary {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "number" &&
    typeof row.app_id === "number" &&
    (row.sql === null ||
      row.sql === undefined ||
      typeof row.sql === "string") &&
    (row.query_id === undefined ||
      row.query_id === null ||
      typeof row.query_id === "number") &&
    (row.description === null || typeof row.description === "string") &&
    typeof row.column_span === "number" &&
    typeof row.sort_order === "number" &&
    (row.tab_id === undefined ||
      row.tab_id === null ||
      typeof row.tab_id === "number") &&
    (row.input_mode === undefined || typeof row.input_mode === "string") &&
    (row.input_config === undefined ||
      row.input_config === null ||
      typeof row.input_config === "object") &&
    (row.show_summary === undefined || typeof row.show_summary === "boolean") &&
    (row.approval_enabled === undefined ||
      typeof row.approval_enabled === "boolean") &&
    (row.approval_config === undefined ||
      row.approval_config === null ||
      isAppTableApprovalConfig(row.approval_config)) &&
    typeof row.created_at === "string" &&
    typeof row.updated_at === "string"
  );
}

export function isAppTableDataItem(value: unknown): value is AppTableDataItem {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "number" &&
    typeof row.column_span === "number" &&
    typeof row.sort_order === "number" &&
    (row.tab_id === undefined ||
      row.tab_id === null ||
      typeof row.tab_id === "number") &&
    Array.isArray(row.columns) &&
    Array.isArray(row.rows) &&
    (row.show_summary === undefined || typeof row.show_summary === "boolean") &&
    (row.approval_enabled === undefined ||
      typeof row.approval_enabled === "boolean") &&
    (row.approval_taxonomy_columns === undefined ||
      (Array.isArray(row.approval_taxonomy_columns) &&
        row.approval_taxonomy_columns.every(
          (entry) => typeof entry === "string",
        ))) &&
    (row.approval_identity_columns === undefined ||
      (Array.isArray(row.approval_identity_columns) &&
        row.approval_identity_columns.every(
          (entry) => typeof entry === "string",
        ))) &&
    (row.approval_follows_ids === undefined ||
      (Array.isArray(row.approval_follows_ids) &&
        row.approval_follows_ids.every((entry) => typeof entry === "number"))) &&
    (row.error === undefined ||
      row.error === null ||
      typeof row.error === "string")
  );
}

export async function fetchAppTables(
  appId: number,
): Promise<AppTableSummary[]> {
  const res = await apiFetch(`/v1/apps/${appId}/tables`, { method: "GET" });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (!Array.isArray(data) || !data.every(isAppTableSummary)) {
    throw new Error("Unexpected response shape");
  }
  return data;
}

export async function fetchAppTablesData(
  appId: number,
): Promise<AppTableDataItem[]> {
  const res = await apiFetch(`/v1/apps/${appId}/tables/data`, {
    method: "GET",
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (typeof data !== "object" || data === null) {
    throw new Error("Unexpected response shape");
  }
  const tables = (data as AppTablesDataResponse).tables;
  if (!Array.isArray(tables) || !tables.every(isAppTableDataItem)) {
    throw new Error("Unexpected response shape");
  }
  return tables;
}

export type AddTableToAppPayload = {
  /** Provide exactly one of `sql` (inline) or `query_id` (reuse a saved query). */
  sql?: string;
  query_id?: number | null;
  description?: string | null;
  display_config?: AppTableDisplayConfig | null;
  column_span?: number;
  tab_id?: number | null;
  input_mode?: AppWidgetInputMode;
  input_config?: AppQueryBuilderCompileRequest | null;
  approval_enabled?: boolean;
  /** Requires `approval_enabled`; the API rejects a taxonomy on a table without approvals. */
  approval_config?: AppTableApprovalConfig | null;
  /** The column this table's rows are paginated by; omit for a widget with no key configured. */
};

export type UpdateAppTablePayload = {
  sql?: string;
  query_id?: number | null;
  description?: string | null;
  display_config?: AppTableDisplayConfig | null;
  column_span?: number;
  tab_id?: number | null;
  input_mode?: AppWidgetInputMode;
  input_config?: AppQueryBuilderCompileRequest | null;
  show_summary?: boolean;
  approval_enabled?: boolean;
  /** Present-and-null clears the stored taxonomy; absent leaves it untouched. */
  approval_config?: AppTableApprovalConfig | null;
  /** Present-and-null clears the configured pagination key; absent leaves it untouched. */
};

export async function addTableToApp(
  appId: number,
  payload: AddTableToAppPayload,
): Promise<AppTableSummary> {
  const res = await apiFetch(`/v1/apps/${appId}/tables`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (!isAppTableSummary(data)) {
    throw new Error("Unexpected response shape");
  }
  return data;
}

export async function updateAppTable(
  appId: number,
  tableId: number,
  payload: UpdateAppTablePayload,
): Promise<AppTableSummary> {
  const res = await apiFetch(`/v1/apps/${appId}/tables/${tableId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (!isAppTableSummary(data)) {
    throw new Error("Unexpected response shape");
  }
  return data;
}

export async function deleteAppTable(
  appId: number,
  tableId: number,
): Promise<void> {
  const res = await apiFetch(`/v1/apps/${appId}/tables/${tableId}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
}

/**
 * Exported for the same reason `isAppTableApprovalConfig` is: rejecting a drill-down does not lose
 * one field, it drops the whole row and the table's cells stop being clickable. A guard stricter
 * than what the API can store is therefore a bug worth pinning directly.
 */
export function isAppTableDrilldown(value: unknown): value is AppTableDrilldown {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "number" &&
    typeof row.trigger_column === "string" &&
    Array.isArray(row.value_columns) &&
    row.value_columns.every((column) => typeof column === "string") &&
    // Optional; an entry is null when that filter column matched no dimension.
    (row.dimension_names === undefined ||
      (Array.isArray(row.dimension_names) &&
        row.dimension_names.every(
          (name) => name === null || typeof name === "string",
        ))) &&
    (row.item_type === "chart" || row.item_type === "table") &&
    typeof row.item_id === "number" &&
    // Optional: a row stored before the setting existed carries no value, and the dialog is what
    // it meant. Refusing it here would reject the whole drill-down and blank its clickable cells.
    (row.presentation === undefined ||
      row.presentation === "dialog" ||
      row.presentation === "drawer")
  );
}

export type AppTableDrilldownPayload = {
  trigger_column: string;
  value_columns: string[];
  item_type: AppDrilldownItemType;
  item_id: number;
  presentation?: AppDrilldownPresentation;
};

export async function fetchAppTableDrilldowns(
  appId: number,
  tableId: number,
): Promise<AppTableDrilldown[]> {
  const res = await apiFetch(`/v1/apps/${appId}/tables/${tableId}/drilldowns`, {
    method: "GET",
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (!Array.isArray(data) || !data.every(isAppTableDrilldown)) {
    throw new Error("Unexpected response shape");
  }
  return data;
}

export async function addAppTableDrilldown(
  appId: number,
  tableId: number,
  payload: AppTableDrilldownPayload,
): Promise<AppTableDrilldown> {
  const res = await apiFetch(`/v1/apps/${appId}/tables/${tableId}/drilldowns`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (!isAppTableDrilldown(data)) {
    throw new Error("Unexpected response shape");
  }
  return data;
}

export async function updateAppTableDrilldown(
  appId: number,
  tableId: number,
  drilldownId: number,
  payload: AppTableDrilldownPayload,
): Promise<AppTableDrilldown> {
  const res = await apiFetch(
    `/v1/apps/${appId}/tables/${tableId}/drilldowns/${drilldownId}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (!isAppTableDrilldown(data)) {
    throw new Error("Unexpected response shape");
  }
  return data;
}

export async function deleteAppTableDrilldown(
  appId: number,
  tableId: number,
  drilldownId: number,
): Promise<void> {
  const res = await apiFetch(
    `/v1/apps/${appId}/tables/${tableId}/drilldowns/${drilldownId}`,
    {
      method: "DELETE",
    },
  );
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
}
